import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  HStack,
  Progress,
  Alert,
  AlertIcon,
  Box,
  useColorModeValue
} from '@chakra-ui/react'
import { Clock, AlertTriangle, LogOut } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'

interface SessionTimeoutModalProps {
  isOpen: boolean
  timeRemainingMinutes: number
  onExtend: () => void
  onLogout: () => void
}

export function SessionTimeoutModal({
  isOpen,
  timeRemainingMinutes,
  onExtend,
  onLogout
}: SessionTimeoutModalProps) {
  const colors = useThemeColors()
  const progressValue = Math.max(0, (timeRemainingMinutes / 5) * 100) // 5 minutes warning period
  const isCritical = timeRemainingMinutes <= 1
  const iconColor = useColorModeValue(
    isCritical ? 'red.500' : 'orange.500',
    isCritical ? 'red.300' : 'orange.300'
  )

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxW="md" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box color={iconColor}>
              {isCritical ? (
                <AlertTriangle size={24} />
              ) : (
                <Clock size={24} />
              )}
            </Box>
            <Text fontSize="lg" fontWeight="bold">
              {isCritical ? 'Session Expiring Soon!' : 'Session Timeout Warning'}
            </Text>
          </HStack>
        </ModalHeader>
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status={isCritical ? "error" : "warning"} borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">
                  {isCritical 
                    ? `Your session will expire in ${timeRemainingMinutes} minute${timeRemainingMinutes !== 1 ? 's' : ''}!`
                    : `Your session will expire in ${timeRemainingMinutes} minutes.`
                  }
                </Text>
                <Text fontSize="sm" mt={1}>
                  {isCritical 
                    ? "Click 'Extend Session' now to continue working, or you'll be automatically logged out."
                    : "Click 'Extend Session' to continue working, or you'll be automatically logged out."
                  }
                </Text>
              </Box>
            </Alert>

            <Box>
              <Text fontSize="sm" color={colors.text} mb={2}>
                Time remaining:
              </Text>
              <Progress 
                value={progressValue} 
                colorScheme={isCritical ? "red" : "orange"}
                size="sm"
                borderRadius="md"
              />
              <Text fontSize="xs" color={colors.textSecondary} mt={1} textAlign="center">
                {timeRemainingMinutes} minute{timeRemainingMinutes !== 1 ? 's' : ''} remaining
              </Text>
            </Box>

            <Text fontSize="sm" color={colors.text}>
              For security reasons, your session will automatically expire after a period of inactivity.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} w="full">
            <Button
              variant="outline"
              colorScheme="gray"
              leftIcon={<LogOut size={16} />}
              onClick={onLogout}
              flex={1}
            >
              Logout Now
            </Button>
            <Button
              colorScheme="blue"
              onClick={onExtend}
              flex={1}
              isLoading={false}
            >
              Extend Session
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

